import React, { useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, Header, Message, Transition } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { save } from "@js/invenio_rdm_records/src/deposit/state/actions/deposit";
import {
  useDepositFormAction,
  useFormConfig,
  useInitialRecord,
} from "../../hooks";
import { ShareDraftButton } from "@js/invenio_app_rdm/deposit/ShareDraftButton";
import { FormTabsProvider, FormFeedbackProvider } from "../../contexts";
import { FormTabs } from "../FormTabs";
import { FormSteps } from "../FormSteps";
import { TabContent } from "../TabContent";
import Overridable from "react-overridable";
import { buildUID } from "react-searchkit";
import {
  SaveButton,
  PreviewButton,
  DeleteButton,
} from "@js/invenio_rdm_records";
import { PublishButton } from "../PublishButton";
import { useFormikContext } from "formik";

export const TabForm = ({ sections = [] }) => {
  const dispatch = useDispatch();
  const record = useSelector((state) => state.deposit.record);
  const actionState = useSelector((state) => state.deposit.actionState);
  const [feedbackDismissed, setFeedbackDismissed] = React.useState(false);
  useEffect(() => {
    setFeedbackDismissed(false);
  }, [actionState]);
  const formFeedbackContextValue = useMemo(
    () => ({
      dismissed: feedbackDismissed,
      setDismissed: setFeedbackDismissed,
    }),
    [feedbackDismissed]
  );
  const saveAction = useCallback(
    (values, params) => dispatch(save(values, params)),
    [dispatch]
  );

  const sectionKeys = useMemo(() => sections.map((s) => s.key), [sections]);
  const { overridableIdPrefix, permissions, formTitle, groupsEnabled } =
    useFormConfig();
  const { initialRecord } = useInitialRecord();
  const formik = useFormikContext();
  const { dirty, values } = formik;
  const reduxState = useSelector((state) => state);

  const params = new URLSearchParams(window.location.search);
  const initialTabKey = params.get("tab");
  const initialStep = sectionKeys.indexOf(initialTabKey);
  const [activeStep, setActiveStepState] = React.useState(
    Math.max(initialStep, 0)
  );
  const [contentVisible, setContentVisible] = React.useState(true);
  // pending step is the step you are switching to. The issue is that when you switch from tabs that both contain
  // a component that is wrapped in dnd provider, the cleanup functionality does not work well, and then
  // you get HTML5 backend error (that you cannot have tow html5 backends). The solution is to fully unmount the content
  // the transition is just to make this less janky
  const [pendingStep, setPendingStep] = React.useState(null);

  const { handleAction: handleSave } = useDepositFormAction({
    action: saveAction,
  });

  // Recompute on every redux action AND on every render (so formik changes flow through).
  // Result is a boolean, so referential identity is stable and re-renders are minimal.
  const tabsLocked = useSelector(
    (state) => !!sections[activeStep]?.lockTabChange?.(formik, state)
  );

  const handleSetStep = useCallback(
    (index) => {
      if (!(index >= 0 && index < sectionKeys.length)) {
        return;
      }
      if (index === activeStep && pendingStep === null) {
        return;
      }
      if (tabsLocked) {
        return;
      }
      const currentSection = sections[activeStep];
      // certain inputs in the form, like the file uploader or community selector, don't actually update the formik state on change, so we trigger a save when changing tabs if there are unsaved changes or if the current section requires it
      // mainly meant for files section and potentially communities. All the other inputs write to formik.
      const shouldSaveOnTabChange =
        typeof currentSection?.saveOnTabChange === "function"
          ? currentSection.saveOnTabChange({
              formikValues: values,
              reduxState,
              dirty,
              activeStep,
            })
          : currentSection?.saveOnTabChange;
      if (dirty || shouldSaveOnTabChange) {
        handleSave();
      }
      const url = new URL(window.location);
      url.searchParams.set("tab", sectionKeys[index]);
      window.history.replaceState({}, "", url);
      // Stage the target step; commit it in onHide so the *old* tab fades out
      // before the new one mounts and fades in.
      setPendingStep(index);
      setContentVisible(false);
    },
    [
      sectionKeys,
      handleSave,
      dirty,
      sections,
      activeStep,
      pendingStep,
      tabsLocked,
      values,
      reduxState,
    ]
  );

  const commitPendingStep = useCallback(() => {
    if (pendingStep !== null) {
      setActiveStepState(pendingStep);
      setPendingStep(null);
    }
    setContentVisible(true);
  }, [pendingStep]);

  useEffect(() => {
    if (sections.length === 0) return;
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    const tab = params.get("tab");

    if (!tab) {
      params.set("tab", sectionKeys[activeStep]);
      url.search = params.toString();
      window.history.replaceState({}, "", url);
    }
  }, [activeStep, sectionKeys, sections.length]);

  useEffect(() => {
    if (sections.length === 0) return;
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab");
      const idx = sectionKeys.indexOf(tab);
      if (idx >= 0) setActiveStepState(idx);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [sectionKeys, sections.length]);

  const next = useCallback(() => {
    if (activeStep < sections.length - 1) {
      handleSetStep(activeStep + 1);
    }
  }, [activeStep, sections.length, handleSetStep]);

  const back = useCallback(() => {
    if (activeStep > 0) {
      handleSetStep(activeStep - 1);
    }
  }, [activeStep, handleSetStep]);

  const formTabContextValue = useMemo(
    () => ({
      activeStep,
      setActiveStep: handleSetStep,
      next,
      back,
    }),
    [activeStep, handleSetStep, next, back]
  );
  if (sections.length === 0) {
    return (
      <Message data-testid="tab-form-no-sections">
        <Message.Header>No sections defined</Message.Header>
        <p>Please provide at least one section to render the TabForm.</p>
      </Message>
    );
  }
  return (
    <FormTabsProvider value={formTabContextValue}>
      <FormFeedbackProvider value={formFeedbackContextValue}>
        <Grid stackable className="tab-form container" data-testid="tab-form">
          {/* Mobile/Tablet: horizontal steps on top */}

          <Grid.Row
            className="mobile tablet only"
            centered
            data-testid="tab-form-steps-row"
          >
            <Grid.Column className="steps-container" width={16}>
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.steps")}
                activeStep={activeStep}
                sections={sections}
                onTabChange={handleSetStep}
                tabsLocked={tabsLocked}
              >
                <FormSteps
                  activeStep={activeStep}
                  sections={sections}
                  onTabChange={handleSetStep}
                  tabsLocked={tabsLocked}
                />
              </Overridable>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            {/* Desktop: vertical tabs on left */}
            <Overridable
              id={buildUID(overridableIdPrefix, "TabForm.tabs")}
              activeStep={activeStep}
              sections={sections}
              onTabChange={handleSetStep}
              tabsLocked={tabsLocked}
            >
              <Grid.Column
                className="computer only form-tabs-column"
                width={5}
                data-testid="tab-form-tabs-column"
              >
                {formTitle && (
                  <Overridable
                    id={buildUID(overridableIdPrefix, "TabForm.formTitle")}
                    formTitle={formTitle}
                  >
                    <Header as="h1" className="form-title">
                      {formTitle}
                    </Header>
                  </Overridable>
                )}
                <FormTabs
                  activeStep={activeStep}
                  sections={sections}
                  onTabChange={handleSetStep}
                  tabsLocked={tabsLocked}
                />
                <Overridable
                  id={buildUID(
                    overridableIdPrefix,
                    "TabForm.FormMetadataSummary"
                  )}
                  record={record}
                  activeStep={activeStep}
                  sections={sections}
                  onTabChange={handleSetStep}
                />
              </Grid.Column>
            </Overridable>

            {/* Content: full width on mobile/tablet, 11 cols on desktop */}
            <Overridable
              id={buildUID(overridableIdPrefix, "TabForm.content")}
              activeStep={activeStep}
              sections={sections}
              next={next}
              back={back}
            >
              <Grid.Column
                computer={11}
                tablet={16}
                mobile={16}
                className="tab-content-column pl-0 pr-0"
                data-testid="tab-form-content-column"
              >
                <Transition
                  visible={contentVisible}
                  animation="fade"
                  duration={100}
                  onHide={commitPendingStep}
                  unmountOnHide
                >
                  <div>
                    <TabContent
                      activeStep={activeStep}
                      sections={sections}
                      next={next}
                      back={back}
                    />
                  </div>
                </Transition>
              </Grid.Column>
            </Overridable>
          </Grid.Row>
          <Overridable id={buildUID(overridableIdPrefix, "TabForm.actions")}>
            <Grid.Row
              data-testid="tab-form-actions-row"
              className="form-actions-row"
            >
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.DeleteButton")}
                permissions={permissions}
              >
                {permissions?.can_delete_draft && record?.id && (
                  <DeleteButton />
                )}
              </Overridable>
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.ShareDraftButton")}
                permissions={permissions}
                groupsEnabled={groupsEnabled}
                initialRecord={initialRecord}
              >
                <div className="share-draft-button-container">
                  {(initialRecord.is_draft === null ||
                    permissions.can_manage) && (
                    <ShareDraftButton
                      record={initialRecord}
                      permissions={permissions}
                      groupsEnabled={groupsEnabled}
                    />
                  )}
                </div>
              </Overridable>
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.PreviewButton")}
              >
                <PreviewButton />
              </Overridable>
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.SaveButton")}
              >
                <SaveButton />
              </Overridable>
              <Overridable
                id={buildUID(overridableIdPrefix, "TabForm.PublishButton")}
                record={initialRecord}
              >
                {/* Wrap in a div because the upstream @js/invenio_rdm_records PublishButton has `fluid` hard-coded in some variants. */}
                <div className="publish-button-container">
                  <PublishButton record={initialRecord} />
                </div>
              </Overridable>
            </Grid.Row>
          </Overridable>
        </Grid>
      </FormFeedbackProvider>
    </FormTabsProvider>
  );
};

TabForm.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      includesPaths: PropTypes.array,
      saveOnTabChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      sectionCompletion: PropTypes.func,
      sectionCompletionThreshold: PropTypes.number,
      /** (formik, reduxState) => boolean — when truthy on the active section, tab navigation is blocked. */
      lockTabChange: PropTypes.func,
      /** component({ record, formConfig, activeStep, next, back, initialRecord }) => ReactNode */
      component: PropTypes.func.isRequired,
    })
  ),
};

export default TabForm;
